/**
 * Hook to intelligently merge estimates and predictions based on dates
 *
 * Simple date-based logic:
 * - Black (past): Anything before current month
 * - Blue (future): Current month or future
 *
 * This runs BEFORE cumulative transformation to ensure correct totals.
 */

import { useMemo } from 'react';
import { UnifiedForecastResult, RebatePoint } from '@erp/shared-types';

/**
 * Data type classification for merged forecast points
 */
export type DataType = 'past' | 'future';

/**
 * Extended RebatePoint with dataType field for color coding
 */
export interface MergedRebatePoint extends RebatePoint {
  dataType: DataType;
}

/**
 * Determines if a date is in the past (before current month)
 */
function isMonthInPast(pointDate: Date, currentDate: Date): boolean {
  const pointYear = pointDate.getUTCFullYear();
  const pointMonth = pointDate.getUTCMonth();
  const currentYear = currentDate.getUTCFullYear();
  const currentMonth = currentDate.getUTCMonth();

  if (pointYear < currentYear) return true;
  if (pointYear === currentYear && pointMonth < currentMonth) return true;
  return false;
}

/**
 * Determines if a date is the current month
 */
function isCurrentMonth(pointDate: Date, currentDate: Date): boolean {
  return (
    pointDate.getUTCFullYear() === currentDate.getUTCFullYear() &&
    pointDate.getUTCMonth() === currentDate.getUTCMonth()
  );
}

/**
 * Smart merge estimates and predictions based on date logic
 */
function smartMergeForecastData(
  forecast: UnifiedForecastResult,
  currentDate: Date,
): UnifiedForecastResult & { merged?: MergedRebatePoint[] } {
  // Get first prediction method (typically 'linear-regression')
  const predictionMethods = Object.keys(forecast.predictions || {});
  const primaryPredictionMethod = predictionMethods[0];
  const predictions = primaryPredictionMethod
    ? forecast.predictions[primaryPredictionMethod] || []
    : [];

  // Helper to get month key (YYYY-MM format)
  const getMonthKey = (date: Date): string => {
    return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;
  };

  // Collect estimates by month (keep latest per month)
  const estimatesByMonth = new Map<string, RebatePoint>();
  (forecast.estimates || []).forEach((point) => {
    if (!point.date) return;
    const monthKey = getMonthKey(point.date);
    const existing = estimatesByMonth.get(monthKey);
    if (!existing || point.date.getTime() > existing.date.getTime()) {
      estimatesByMonth.set(monthKey, point);
    }
  });

  // Collect predictions by month (keep latest per month)
  const predictionsByMonth = new Map<string, RebatePoint>();
  predictions.forEach((point) => {
    if (!point.date) return;
    const monthKey = getMonthKey(point.date);
    const existing = predictionsByMonth.get(monthKey);
    if (!existing || point.date.getTime() > existing.date.getTime()) {
      predictionsByMonth.set(monthKey, point);
    }
  });

  // Merge estimates and predictions based on simple date logic
  const mergedPoints: MergedRebatePoint[] = [];
  const allMonthKeys = new Set([
    ...Array.from(estimatesByMonth.keys()),
    ...Array.from(predictionsByMonth.keys()),
  ]);

  // Process each month
  Array.from(allMonthKeys)
    .sort() // Sort chronologically
    .forEach((monthKey) => {
      const estimate = estimatesByMonth.get(monthKey);
      const prediction = predictionsByMonth.get(monthKey);

      // Determine if this month is past or current/future
      const referenceDate = estimate?.date || prediction?.date;
      if (!referenceDate) return;

      const isPast = isMonthInPast(referenceDate, currentDate);
      // Simple logic: past = black, current/future = blue
      const dataType: DataType = isPast ? 'past' : 'future';

      // Determine which point to use (prefer estimate if it has any data, otherwise prediction)
      let pointToUse: RebatePoint;
      if (estimate && estimate.totalRebateAmount > 0) {
        pointToUse = estimate;
      } else if (prediction && prediction.totalRebateAmount > 0) {
        pointToUse = prediction;
      } else if (estimate) {
        pointToUse = estimate;
      } else if (prediction) {
        pointToUse = prediction;
      } else {
        // No data available, skip
        return;
      }

      mergedPoints.push({
        ...pointToUse,
        dataType,
      });
    });

  // Sort by date (using UTC timestamps)
  mergedPoints.sort((a, b) => {
    if (!a.date || !b.date) return 0;
    return a.date.getTime() - b.date.getTime();
  });

  return {
    ...forecast,
    // Keep original estimates and predictions for chart visualization
    estimates: forecast.estimates,
    predictions: forecast.predictions,
    // Add merged array for tables and other use cases that need single timeline
    merged: mergedPoints,
  };
}

/**
 * Hook to smart merge forecast data based on current date
 *
 * @param forecastData - The raw forecast data with separate estimates and predictions
 * @param currentDate - The current date to use for past/present/future determination (defaults to now in UTC)
 * @returns Merged forecast data with smart date-based logic applied
 */
export function useSmartMergedForecastData(
  forecastData: UnifiedForecastResult | null | undefined,
  currentDate?: Date,
):
  | (UnifiedForecastResult & { merged?: MergedRebatePoint[] })
  | null
  | undefined {
  // Normalize current date to UTC midnight to avoid timezone issues
  const normalizedCurrentDate = useMemo(() => {
    const date = currentDate || new Date();
    return new Date(
      Date.UTC(
        date.getUTCFullYear(),
        date.getUTCMonth(),
        date.getUTCDate(),
        0,
        0,
        0,
        0,
      ),
    );
  }, [currentDate]);

  return useMemo(() => {
    if (!forecastData) return forecastData;
    return smartMergeForecastData(forecastData, normalizedCurrentDate);
  }, [forecastData, normalizedCurrentDate]);
}
